/** @jsx isolateComponent */
import { isFunction } from 'lodash';
import { ReactNode, useCallback, useState } from 'react';
import Dropzone from 'react-dropzone';

import { isolateComponent, withModifier } from '@zapier/style-encapsulation';
import { addLocationOriginToUrl } from '@zapier/url-utils';
import {
  Alert,
  Animation,
  Colors,
  Link,
  Spacer,
  Typography,
} from '@zapier/design-system';

import TextInput from '../TextInput';
import { postJson } from '@zapier/toolbox-browser-fetch';

const ZAPIER_API_BASE_URL = process.env.ZAPIER_API_BASE_URL || undefined;

// Passing ZAPIER_API_BASE_URL as the origin so that this ImageUpload component
// will use the correct Zapier origin in the Partner Contact form
// hosted at developer.zapier.
const UPLOAD_URL = addLocationOriginToUrl(
  '/photos/upload/eae107c604c0875fc4346201e58fb027',
  ZAPIER_API_BASE_URL
);

export type ImageUploadField = {
  /** Additional content to describe this field. */
  details?: ReactNode;
  /** The unique ID of this field. */
  id: string;
  /** The label that describes this field. */
  label: string;
  /** Identifies this field type. */
  type: 'image';
};

export type ImageUploadProps = {
  /** The callback function to fire when the field changes. */
  onChange: (value: string) => void;
  /** Static properties about this field. */
  staticProperties: ImageUploadField;
  /** A prefilled value from some higher state. */
  value: string;
};

type DerivedState = {
  error: string;
  isDragActive: boolean;
  isFocused: boolean;
};

// The file types that are supported by the backend endpoint.
const MIMETYPES = ['image/jpeg', 'image/png', 'image/gif'].join(', ');

const Styles = {
  dropzone: (derivedState: DerivedState) => [
    {
      ...Typography.Body2,
      marginBottom: '10px',
      border: `2px dashed ${Colors.GrayWarm6}`,
      borderRadius: '5px',
      height: '90px',
      padding: '10px 15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: `border-color ${Animation.transitionDuration} ${Animation.transitionTimingFunction}`,
      '&:hover': {
        borderColor: Colors.UiPrimaryStrongest,
      },
    },
    (derivedState.isFocused || derivedState.isDragActive) &&
      withModifier('focused', {
        borderColor: Colors.UiPrimaryStrongest,
      }),
  ],
  selectOne: {
    color: Colors.UiPrimaryStronger,
    paddingLeft: '3px',
    textDecoration: 'underline',
    '&:hover': {
      color: Colors.UiPrimaryStrongest,
    },
  },
  root: {
    display: 'grid',
    gridGap: 10,
  },
};

const UPLOAD_ERROR_STR = `We're having trouble uploading that file. Hopefully it's temporary, and it will work if you try again. Please let our support team know if the error persists!`;

type OnUpload = (url: string) => void;
type OnError = (err: string) => void;

export const uploadFile = (
  file: File,
  onUpload: OnUpload,
  onError: OnError
) => {
  const reader = new FileReader();

  reader.onabort = () => {
    onError(UPLOAD_ERROR_STR);
  };

  reader.onerror = () => {
    onError(UPLOAD_ERROR_STR);
  };

  reader.onload = async () => {
    const base64 = reader.result;
    if (typeof base64 !== 'string') {
      onError(UPLOAD_ERROR_STR);
      return;
    }
    const image = base64.split(',')[1];
    const data = { image };
    try {
      // ts doesn't like that postJson returns void : (
      // tslint:disable-next-line
      const responseData = await postJson(UPLOAD_URL, data);
      if (responseData && responseData.link) {
        onUpload(addLocationOriginToUrl(responseData.link));
      } else {
        onError(UPLOAD_ERROR_STR);
      }
    } catch (err) {
      onError(UPLOAD_ERROR_STR);
    }
  };

  reader.readAsDataURL(file);
};

const createUploader = ({
  onChange,
  staticProperties: { details },
  value,
}: ImageUploadProps) => () => {
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState('');

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      const onUpload: OnUpload = url => onChange(`${url} ${value}`.trim());
      setIsFocused(false);
      if (rejectedFiles.length) {
        setError(UPLOAD_ERROR_STR);
      } else {
        acceptedFiles.forEach((file: File) => {
          uploadFile(file, onUpload, setError);
        });
      }
    },
    /**
     * Honestly, I don't understand why React thinks this is unnecessary.
     *
     * When onChange is not a hook dependency, we call an old version of
     * onChange with stale data and can possibly overwrite the data in other
     * fields. It needs to be a dep so that we call the most updated version.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange]
  );

  return (
    <div css={Styles.root}>
      {!details && <Spacer height={5} />}
      {details && <div>{isFunction(details) ? details() : details}</div>}
      <Dropzone onDrop={onDrop}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            css={Styles.dropzone({ error, isDragActive, isFocused })}
            onBlur={() => setIsFocused(false)}
            onFocus={() => setIsFocused(true)}
          >
            <input {...getInputProps()} accept={MIMETYPES} />
            <div>
              Drop an image here or
              <span css={Styles.selectOne}>select one from your device</span>
            </div>
          </div>
        )}
      </Dropzone>
      {error && (
        <Alert onDismiss={() => setError('')} status="error">
          {error}
        </Alert>
      )}
      <div>
        Uploaded image URLs will appear below.{' '}
        <Link href="https://www.take-a-screenshot.org/" target="_blank">
          Click here for easy-to-follow instructions for taking a screenshot.
        </Link>{' '}
        You can also provide URLs of images that were already uploaded somewhere
        else, e.g. via{' '}
        <Link href="https://zapier.com/zappy" target="_blank">
          Zappy
        </Link>
        .
      </div>
    </div>
  );
};

const ImageUpload = (props: ImageUploadProps) => {
  const { onChange, staticProperties, value } = props;
  return (
    <TextInput
      onChange={onChange}
      staticProperties={{
        ...staticProperties,
        details: createUploader(props),
        type: 'text',
      }}
      value={value}
    />
  );
};

export default ImageUpload;
